/**
 * OCR parsing functions for Bangladeshi NID cards
 */

import { formatDateOfBirth } from './dateUtils';
import { cleanName, extractUntilLabel } from './imageUtils';
import {
  BANGLA_NAME_PATTERNS,
  CHAR_PATTERNS,
  DOB_PATTERNS,
  ENGLISH_NAME_PATTERNS,
  FALSE_POSITIVE_PATTERNS,
  NAME_LABEL_PATTERNS,
  NID_PATTERNS,
  SMART_FATHER_PATTERN,
  YEAR_PATTERN
} from './ocrPatterns';

/**
 * Parse NID number from OCR text
 * @param {string} text - OCR text
 * @returns {string} NID number
 */
export const parseNidNumber = (text) => {
  let nidNumber = '';

  // Strategy 1: Look for NID number with explicit labels
  for (const pattern of NID_PATTERNS.LABELED) {
    const match = text.match(pattern);
    if (match && match[1]) {
      const cleanedNid = match[1].replace(/\s/g, '').trim();
      if (cleanedNid.length >= 10 && cleanedNid.length <= 17) {
        nidNumber = cleanedNid;
        break;
      }
    }
  }

  // Strategy 2: Look for standalone 10, 13, or 17 digit numbers
  if (!nidNumber) {
    const standaloneNidPattern = NID_PATTERNS.STANDALONE;
    const matches = [];
    let match;

    while ((match = standaloneNidPattern.exec(text)) !== null) {
      const cleanedMatch = match[1].replace(/\s/g, '');
      matches.push(cleanedMatch);
    }

    // Filter out common false positives
    const filteredMatches = matches.filter(nid => {
      if (YEAR_PATTERN.test(nid)) return false;
      if (nid.length === 10 && nid.startsWith('0')) return false;
      return true;
    });

    // Prefer longer NID numbers
    for (const match of filteredMatches) {
      if (match.length >= 10) {
        if (!nidNumber || match.length > nidNumber.length) {
          nidNumber = match;
        }
      }
    }
  }

  // Strategy 3: Last resort - extract any 10+ digit number from text
  if (!nidNumber) {
    const embeddedNidPattern = NID_PATTERNS.EMBEDDED;
    const matches = text.match(embeddedNidPattern);

    if (matches) {
      const filteredMatches = matches.map(nid => nid.replace(/\s/g, '')).filter(nid => {
        if (YEAR_PATTERN.test(nid)) return false;
        if (nid.length === 10 && nid.startsWith('0')) return false;
        const contextMatch = text.match(new RegExp(`(?:the|of|সরকার)\\s*${nid}`, 'i'));
        if (contextMatch) return false;
        return true;
      });

      for (const match of filteredMatches) {
        if (match.length >= 10) {
          if (!nidNumber || match.length > nidNumber.length) {
            nidNumber = match;
          }
        }
      }
    }
  }

  return nidNumber;
};

/**
 * Parse date of birth from OCR text
 * @param {string} text - OCR text
 * @returns {string} Formatted date of birth
 */
export const parseDateOfBirth = (text) => {
  const dobMatch = text.match(DOB_PATTERNS.MAIN);
  if (dobMatch && dobMatch[1]) {
    const formattedDate = formatDateOfBirth(dobMatch[1]);
    return formattedDate;
  }
  return '';
};

/**
 * Parse English name from OCR text
 * @param {string} text - OCR text
 * @returns {string} English name
 */
export const parseEnglishName = (text) => {
  let nameEnglish = '';

  for (const pattern of ENGLISH_NAME_PATTERNS) {
    const match = text.match(pattern);
    if (match && match[1] && !nameEnglish) {
      const englishName = match[1].trim();
      if (englishName.length > 2 && englishName.length < 50 && !/\d/.test(englishName)) {
        if (!FALSE_POSITIVE_PATTERNS.ENGLISH_NAME.test(englishName)) {
          nameEnglish = cleanName(englishName);
          break;
        }
      }
    }
  }

  // Fallback: Try to extract any proper name pattern
  if (!nameEnglish) {
    const namePatterns = [
      /([A-Z]\.[A-Z]\.\s+[A-Z][a-z]+(?:\s+[A-Z][a-z]+)*)/g,
      /([A-Z][a-z]+\s+[A-Z][a-z]+(?:\s+[A-Z][a-z]+)*)/g
    ];

    for (const pattern of namePatterns) {
      const matches = text.match(pattern);
      if (matches) {
        for (const match of matches) {
          const cleanMatch = match.trim();
          if (!/নাম|পিতা|মাতা|Father|Mother|Name/i.test(cleanMatch) &&
              !/\d/.test(cleanMatch) &&
              cleanMatch.length > 2 &&
              cleanMatch.length < 50 &&
              !nameEnglish) {
            if (!FALSE_POSITIVE_PATTERNS.ENGLISH_NAME.test(cleanMatch)) {
              nameEnglish = cleanName(cleanMatch);
              break;
            }
          }
        }
        if (nameEnglish) break;
      }
    }
  }

  return nameEnglish;
};

/**
 * Parse Bangla name from OCR text
 * @param {string} text - OCR text
 * @returns {string} Bangla name
 */
export const parseBanglaName = (text) => {
  let nameBangla = '';

  for (const pattern of BANGLA_NAME_PATTERNS) {
    const match = text.match(pattern);
    if (match && match[1] && !nameBangla) {
      const banglaNameText = match[1].trim();
      const banglaOnly = banglaNameText.replace(/[^\u0980-\u09FF\s\.]/g, '').trim();
      if (banglaOnly.length > 2 && banglaOnly.length < 50 && !/\d/.test(banglaOnly)) {
        if (!FALSE_POSITIVE_PATTERNS.BANGLA_NAME.test(banglaOnly)) {
          nameBangla = cleanName(banglaOnly);
          break;
        }
      }
    }
  }

  return nameBangla;
};

/**
 * Parse father's name from OCR text
 * @param {string} text - OCR text
 * @returns {string} Father's name
 */
export const parseFatherName = (text) => {
  let fatherName = '';

  const fatherMatch = text.match(NAME_LABEL_PATTERNS.FATHER);
  if (fatherMatch) {
    const fatherIndex = text.indexOf(fatherMatch[0]);
    const afterFather = text.substring(fatherIndex + fatherMatch[0].length);

    const fatherText = extractUntilLabel(afterFather, [
      NAME_LABEL_PATTERNS.MOTHER,
      NAME_LABEL_PATTERNS.NAME,
      DOB_PATTERNS.MAIN,
      NAME_LABEL_PATTERNS.BLOOD_GROUP,
      NAME_LABEL_PATTERNS.NID
    ]);

    if (fatherText && fatherText.length > 2) {
      fatherName = cleanName(fatherText);
    }
  }

  // Special handling for smart NID cards
  if (!fatherName) {
    const smartFatherMatch = text.match(SMART_FATHER_PATTERN);
    if (smartFatherMatch && smartFatherMatch[1]) {
      const fatherText = smartFatherMatch[1].trim();
      const fatherOnly = fatherText.replace(/[^\u0980-\u09FF\s]/g, '').trim();
      const fatherWords = fatherOnly.split(/\s+/);
      const filteredFather = fatherWords.slice(0, 2).join(' ');
      if (filteredFather.length > 2) {
        fatherName = filteredFather;
      }
    }
  }

  return fatherName;
};

/**
 * Parse mother's name from OCR text
 * @param {string} text - OCR text
 * @returns {string} Mother's name
 */
export const parseMotherName = (text) => {
  let motherName = '';

  const motherMatch = text.match(NAME_LABEL_PATTERNS.MOTHER);
  if (motherMatch) {
    const motherIndex = text.indexOf(motherMatch[0]);
    const afterMother = text.substring(motherIndex + motherMatch[0].length);

    const motherText = extractUntilLabel(afterMother, [
      NAME_LABEL_PATTERNS.FATHER,
      NAME_LABEL_PATTERNS.NAME,
      DOB_PATTERNS.MAIN,
      NAME_LABEL_PATTERNS.BLOOD_GROUP,
      NAME_LABEL_PATTERNS.NID
    ]);

    if (motherText && motherText.length > 2) {
      motherName = cleanName(motherText);
    }
  }

  return motherName;
};

/**
 * Parse address from NID back OCR text
 * @param {string} text - OCR text
 * @returns {string} Address
 */
export const parseAddress = (text) => {
  // Extract address by looking for Bangla text patterns
  // This is a simplified approach - you may need to enhance based on actual NID formats
  const addressLines = text.split('\n').filter(line => {
    const trimmedLine = line.trim();
    // Skip empty lines and lines that are too short
    if (trimmedLine.length < 5) return false;
    // Skip lines that look like headers or labels
    if (/গণপ্রজাতন্ত্রী|সরকার|বাংলাদেশ|জাতীয়|পরিচয়পত্র|Government|Republic|Card/i.test(trimmedLine)) return false;
    // Include lines with Bangla text
    return CHAR_PATTERNS.BANGLA_TEXT.test(trimmedLine);
  });

  const address = addressLines.join(', ').trim();
  return address;
};

/**
 * Parse front side of NID card
 * @param {string} text - OCR text from front side
 * @returns {Object} Parsed data
 */
export const parseNidFront = (text) => {
  return {
    nameEnglish: parseEnglishName(text),
    nameBangla: parseBanglaName(text),
    fatherName: parseFatherName(text),
    motherName: parseMotherName(text),
    nidNumber: parseNidNumber(text),
    dateOfBirth: parseDateOfBirth(text)
  };
};

/**
 * Parse back side of NID card
 * @param {string} text - OCR text from back side
 * @returns {Object} Parsed data
 */
export const parseNidBack = (text) => {
  return {
    address: parseAddress(text)
  };
};
